import React from "react";
import { Link } from "gatsby";
import { RouteComponentProps } from "@reach/router";
import Layout from "../core/Layout";
import NewsletterRow from "../newsletter/NewsletterRow";
import { OutboundLink } from "gatsby-plugin-google-gtag";

interface IftttEntry {
  classicAndroid: string;
  compose: string;
  documentationUrl: string;
  exampleUrl: string;
}

interface ReverseIfThisThenThatProps extends RouteComponentProps {
  pageContext: {
    compose: string;
    classicAndroid: string;
    documentationUrl: string;
    exampleUrl: string;
    iftttArray: IftttEntry[];
  };
}

export default function ReverseIfThisThenThat(
  props: ReverseIfThisThenThatProps
) {
  const { compose, classicAndroid, documentationUrl, exampleUrl, iftttArray } =
    props.pageContext;

  const normalizedCompose = compose.split(" ").join("-").replace(/[./]/g, "");
  const pageSlug = `/What-is-the-equivalent-of-${normalizedCompose}-in-Android-Views`;

  const pageTitle = `What's the equivalent of ${compose} in Android Views? | JetpackCompose.app`;
  const pageDescription = `${compose} in Jetpack Compose is equivalent to ${classicAndroid} in classic Android Views. Learn the mapping between Compose and View APIs.`;

  // Find related mappings (similar category based on naming patterns)
  const composeKeywords = compose.toLowerCase().split(/[\s/.]+/);
  const relatedMappings = iftttArray
    .filter((entry) => {
      const entryKeywords = entry.compose.toLowerCase().split(/[\s/.]+/);
      return (
        entry.compose !== compose &&
        composeKeywords.some(
          (keyword) =>
            keyword.length > 3 &&
            entryKeywords.some((ek) => ek.includes(keyword))
        )
      );
    })
    .slice(0, 5);

  return (
    <Layout
      maxWidth="lg"
      pageTitle={pageTitle}
      pageDescription={pageDescription}
      pageSlug={pageSlug}
      pageImageUrl="/JetpackComposeIfThen_Preview_Image.png"
      seoImageUrl="/JetpackComposeIfThen_Preview_Image.png"
    >
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm">
          <Link
            to="/What-is-the-equivalent-of-X-in-Jetpack-Compose"
            className="text-indigo-600 hover:underline"
          >
            API Migration Guide
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-600">{compose}</span>
        </nav>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            What's the equivalent of{" "}
            <span className="text-green-600">{compose}</span> in Android Views?
          </h1>

          {/* Answer Card */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">In Classic Android, use:</p>
                <p className="text-xl font-semibold text-gray-900">
                  {classicAndroid}
                </p>
              </div>
            </div>
          </div>

          {/* Mapping Visualization */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 py-6 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg px-6 py-4 text-center">
              <p className="text-xs text-green-600 font-medium mb-1">
                Jetpack Compose
              </p>
              <p className="text-lg font-mono font-semibold text-green-800">
                {compose}
              </p>
            </div>
            <div className="flex items-center">
              <svg
                className="w-8 h-8 text-gray-400 transform md:rotate-0 rotate-90"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-4 text-center">
              <p className="text-xs text-gray-600 font-medium mb-1">
                Classic Android
              </p>
              <p className="text-lg font-mono font-semibold text-gray-800">
                {classicAndroid}
              </p>
            </div>
          </div>

          {/* Links */}
          <div className="flex flex-wrap gap-3">
            {documentationUrl && (
              <OutboundLink
                href={documentationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
                View Documentation
              </OutboundLink>
            )}
            {exampleUrl && (
              <OutboundLink
                href={exampleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                View Example
              </OutboundLink>
            )}
            <Link
              to={`/What-is-the-equivalent-of-${classicAndroid
                .split(" ")
                .join("-")}-in-Jetpack-Compose`}
              className="inline-flex items-center gap-2 border border-green-300 text-green-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-50 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                />
              </svg>
              View Reverse Mapping
            </Link>
          </div>
        </div>

        {/* Related Mappings */}
        {relatedMappings.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Related API Mappings
            </h2>
            <div className="space-y-3">
              {relatedMappings.map((mapping, index) => {
                const slug = mapping.compose
                  .split(" ")
                  .join("-")
                  .replace(/[./]/g, "");
                return (
                  <Link
                    key={index}
                    to={`/What-is-the-equivalent-of-${slug}-in-Android-Views`}
                    className="block p-4 bg-white rounded-lg border border-gray-100 hover:border-indigo-200 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-green-600 font-medium">
                          {mapping.compose}
                        </span>
                        <span className="text-gray-400">→</span>
                        <span className="text-gray-600">
                          {mapping.classicAndroid}
                        </span>
                      </div>
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Browse All */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Browse All API Mappings
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Link
              to="/What-is-the-equivalent-of-X-in-Jetpack-Compose"
              className="p-4 bg-green-50 rounded-lg border border-green-200 hover:bg-green-100 transition-colors text-center"
            >
              <p className="font-medium text-green-800">
                Classic Android → Compose
              </p>
              <p className="text-sm text-green-600">
                Find Compose equivalents for Android Views
              </p>
            </Link>
            <Link
              to="/What-is-the-equivalent-of-X-in-Android-Views"
              className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors text-center"
            >
              <p className="font-medium text-gray-800">
                Compose → Classic Android
              </p>
              <p className="text-sm text-gray-600">
                Find Android View equivalents for Compose
              </p>
            </Link>
          </div>
        </div>

        {/* Newsletter CTA */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-8 mb-8">
          <NewsletterRow tagline="Get the complete migration cheatsheet in your inbox" />
        </div>

        {/* Back Link */}
        <div className="text-center">
          <Link
            to="/What-is-the-equivalent-of-X-in-Jetpack-Compose"
            className="inline-flex items-center gap-2 text-indigo-600 hover:underline"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to API Migration Guide
          </Link>
        </div>
      </div>
    </Layout>
  );
}
